import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { auth, storage } from './firebase';

export const TRAVEL_PLAN_INPUT_PREFIX = 'travel-plan-inputs';
export const TRAVEL_PLAN_INPUT_MAX_FILES = 6;
export const TRAVEL_PLAN_INPUT_MAX_BYTES = 8 * 1024 * 1024;

export type TravelPlanAttachment = {
  storagePath: string;
  contentType: string;
  fileName: string;
  downloadUrl?: string;
};

export type TravelPlanLocalFile = {
  id: string;
  file: File;
  previewUrl: string;
  name: string;
};

export function isTravelPlanImage(file: File): boolean {
  return file.type.startsWith('image/') && file.size > 0 && file.size <= TRAVEL_PLAN_INPUT_MAX_BYTES;
}

export function filesFromClipboard(data: DataTransfer | null): File[] {
  if (!data) return [];
  const fromFiles = Array.from(data.files || []).filter(isTravelPlanImage);
  if (fromFiles.length) return fromFiles;
  const items = Array.from(data.items || []);
  const blobs: File[] = [];
  for (const item of items) {
    if (!item.type.startsWith('image/')) continue;
    const blob = item.getAsFile();
    if (blob && isTravelPlanImage(blob)) blobs.push(blob);
  }
  return blobs;
}

export function appendLocalPlanFiles(
  current: TravelPlanLocalFile[],
  incoming: File[],
): TravelPlanLocalFile[] {
  const next = [...current];
  for (const file of incoming) {
    if (!isTravelPlanImage(file)) continue;
    if (next.length >= TRAVEL_PLAN_INPUT_MAX_FILES) break;
    next.push({
      id: `img_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      file,
      previewUrl: URL.createObjectURL(file),
      name: file.name || 'screenshot.png',
    });
  }
  return next;
}

export function revokeLocalPlanFiles(files: TravelPlanLocalFile[]) {
  for (const row of files) URL.revokeObjectURL(row.previewUrl);
}

function sanitizeFileName(name: string): string {
  const trimmed = name.trim() || 'screenshot.png';
  return trimmed.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 80);
}

/** Upload CRM planner screenshots. The Grok bot later reads these via signed URLs. */
export async function uploadTravelPlanInputs(
  files: TravelPlanLocalFile[],
): Promise<TravelPlanAttachment[]> {
  const user = auth.currentUser;
  if (!user || user.isAnonymous) throw new Error('Sign in required');
  const batchId = `batch_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const uploaded: TravelPlanAttachment[] = [];

  for (let i = 0; i < files.length; i++) {
    const row = files[i];
    const fileName = sanitizeFileName(row.name);
    const storagePath = `${TRAVEL_PLAN_INPUT_PREFIX}/${user.uid}/${batchId}/${i}-${fileName}`;
    const storageRef = ref(storage, storagePath);
    const contentType = row.file.type || 'image/png';
    await uploadBytes(storageRef, row.file, { contentType });
    const downloadUrl = await getDownloadURL(storageRef);
    uploaded.push({ storagePath, contentType, fileName, downloadUrl });
  }

  return uploaded;
}
